import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { errorMessage, isApiError } from '@/api/client';
import { useAuth } from '@/auth/context';
import { AFTER_AUTH_KEY } from '@/auth/guards';
import { session } from '@/lib/storage';
import { Button, ErrorNote, TextField } from '@/components/ui';
import { browserTimezone } from '@/lib/timezones';
import { AuthLayout, linkClass } from './AuthLayout';

export function SignUp() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const zone = browserTimezone();

  async function submit(e: FormEvent) {
    e.preventDefault();
    const f: Record<string, string> = {};
    if (!form.name.trim()) f.name = 'Tell us your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) f.email = 'Enter a valid email address.';
    if (form.password.length < 8) f.password = 'Use at least 8 characters.';
    setFields(f);
    setError(null);
    if (Object.keys(f).length) return;
    setBusy(true);
    try {
      session.set(AFTER_AUTH_KEY, '/onboarding');
      await signUp({ name: form.name.trim(), email: form.email.trim(), password: form.password, timezone: zone });
      navigate('/onboarding', { replace: true });
    } catch (err) {
      session.remove(AFTER_AUTH_KEY);
      if (isApiError(err)) setFields(err.fields);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Connect your apps. See your numbers."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/sign-in" className={linkClass}>
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {error && <ErrorNote title="Couldn’t create the account" message={error} />}
        <TextField label="Your name" autoComplete="name" value={form.name} onChange={set('name')} error={fields.name} autoFocus />
        <TextField label="Email" type="email" autoComplete="email" value={form.email} onChange={set('email')} error={fields.email} />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={set('password')}
          error={fields.password}
          hint="At least 8 characters."
        />
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          Create account
        </Button>
        <p className="text-center text-xs text-muted-ink">
          We’ll use your time zone ({zone}) for “today”. You can change it later.
        </p>
      </form>
    </AuthLayout>
  );
}
